import { useCallback, useEffect, useRef, useState } from "react";

import * as service from "../services/creatorPopsService";

const normalizePops = (items) =>
  Array.isArray(items)
    ? items.filter((pop) => pop && typeof pop === "object")
    : [];

const getPopKey = (pop) => {
  if (pop?.popId != null) {
    return `id:${pop.popId}`;
  }

  if (pop?.popTag) {
    return `tag:${pop.popTag}`;
  }

  return null;
};

const mergeUniquePops = (current, incoming) => {
  const map = new Map();

  [...current, ...incoming].forEach((pop) => {
    const key = getPopKey(pop);

    if (key) {
      map.set(key, pop);
    }
  });

  return Array.from(map.values());
};

const unwrapResponse = (response) => {
  const body = response?.data ?? response;

  if (body?.success === false) {
    throw new Error(body.message || "Request failed.");
  }

  return body?.data &&
    typeof body.data === "object" &&
    !Array.isArray(body.data) &&
    !body.pops &&
    !body.pop
    ? body.data
    : body;
};

const parseResponse = (response) => {
  const payload = unwrapResponse(response);

  return {
    pops: normalizePops(Array.isArray(payload) ? payload : payload?.pops),
    pagination: payload?.pagination || {},
  };
};

const getErrorMessage = (error, fallback) =>
  error?.response?.data?.message || error?.message || fallback;

export const useCreatorPops = (username, limit = 12) => {
  const pageSize = Math.max(1, Number(limit) || 12);

  const [pops, setPops] = useState([]);

  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [loadingEdit, setLoadingEdit] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [adding, setAdding] = useState(false);
  const [searching, setSearching] = useState(false);

  const [error, setError] = useState("");
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  const offsetRef = useRef(0);
  const queryRef = useRef("");
  const requestingRef = useRef(false);
  const requestIdRef = useRef(0);

  const fetchPops = useCallback(
    async ({ reset = false, query } = {}) => {
      if (!username) return false;

      if (!reset && requestingRef.current) {
        return false;
      }

      const searchValue =
        query === undefined ? queryRef.current : String(query || "").trim();

      const offset = reset ? 0 : offsetRef.current;

      const requestId = ++requestIdRef.current;

      requestingRef.current = true;

      if (reset) {
        setLoading(true);
        setSearching(Boolean(searchValue));
        setLoadingMore(false);
      } else {
        setLoadingMore(true);
      }

      setError("");

      try {
        const response = searchValue
          ? await service.searchCreatorPops(username, searchValue, {
              limit: pageSize,
              offset,
            })
          : await service.getCreatorPops(username, {
              limit: pageSize,
              offset,
            });

        if (requestId !== requestIdRef.current) {
          return false;
        }

        const { pops: received, pagination } = parseResponse(response);

        const nextOffset =
          Number.isSafeInteger(Number(pagination.nextOffset)) &&
          Number(pagination.nextOffset) > offset
            ? Number(pagination.nextOffset)
            : offset + received.length;

        offsetRef.current = nextOffset;

        setPops((current) =>
          reset
            ? mergeUniquePops([], received)
            : mergeUniquePops(current, received),
        );

        const reportedTotal = Number(pagination.total);

        const hasReportedTotal =
          pagination.total != null && Number.isFinite(reportedTotal);

        setTotal((previous) =>
          hasReportedTotal
            ? reportedTotal
            : reset
              ? received.length
              : Math.max(previous, nextOffset),
        );

        setHasMore(
          typeof pagination.hasMore === "boolean"
            ? pagination.hasMore
            : hasReportedTotal
              ? nextOffset < reportedTotal
              : received.length >= pageSize,
        );

        return {
          pops: received,
          pagination,
        };
      } catch (err) {
        if (requestId !== requestIdRef.current) {
          return false;
        }

        console.error("getCreatorPops failed:", err);

        setError(getErrorMessage(err, "Unable to load creator Pops."));

        if (reset) {
          setPops([]);
          setTotal(0);
          setHasMore(false);
        }

        return false;
      } finally {
        if (requestId === requestIdRef.current) {
          requestingRef.current = false;
          setLoading(false);
          setLoadingMore(false);
          setSearching(false);
        }
      }
    },
    [username, pageSize],
  );

  const getCreatorPops = useCallback(
    (options = {}) => fetchPops(options),
    [fetchPops],
  );

  const searchCreatorPops = useCallback(
    (value) => {
      const nextQuery = String(value || "").trim();

      queryRef.current = nextQuery;
      setSearchQuery(nextQuery);

      return fetchPops({
        reset: true,
        query: nextQuery,
      });
    },
    [fetchPops],
  );

  const getCreatorPopDetails = useCallback(
    async (popTag, targetUser = username) => {
      if (!popTag || !targetUser) {
        return false;
      }

      try {
        setLoadingEdit(true);
        setError("");

        const response = await service.getCreatorPopDetails(popTag, targetUser);

        const result = unwrapResponse(response);

        return {
          pop: result?.pop || null,
        };
      } catch (err) {
        console.error("getCreatorPopDetails failed:", err);

        setError(getErrorMessage(err, "Unable to load Pop edit data."));

        return false;
      } finally {
        setLoadingEdit(false);
      }
    },
    [username],
  );

  const addCreatorPop = useCallback(
    async (data) => {
      if (!username || !data) {
        return false;
      }

      try {
        setAdding(true);
        setError("");

        const response = await service.addCreatorPop(username, data);

        const result = unwrapResponse(response);

        if (result?.pop) {
          if (!queryRef.current) {
            setPops((current) =>
              mergeUniquePops(
                [result.pop],
                current.filter(
                  (pop) => String(pop.popTag) !== String(result.pop.popTag),
                ),
              ),
            );
          }

          setTotal((current) => current + 1);
        }

        return result;
      } catch (err) {
        console.error("addCreatorPop failed:", err);

        setError(getErrorMessage(err, "Unable to create Pop."));

        return false;
      } finally {
        setAdding(false);
      }
    },
    [username],
  );

  const updateCreatorPopDetails = useCallback(
    async (popTag, data) => {
      if (!popTag || !username || !data) {
        return false;
      }

      try {
        setSaving(true);
        setError("");

        const payload = {
          title: String(data.title || "").trim(),
          tags: String(data.tags || "").trim(),
          visibility: String(data.visibility || "private")
            .trim()
            .toLowerCase(),
        };

        if (!payload.title) {
          throw new Error("Pop title is required.");
        }

        if (!["public", "unlisted", "private"].includes(payload.visibility)) {
          throw new Error("Invalid Pop visibility.");
        }

        const response = await service.updateCreatorPopDetails(
          popTag,
          username,
          payload,
        );

        const result = unwrapResponse(response);

        setPops((current) =>
          current.map((pop) =>
            String(pop.popTag) === String(popTag)
              ? {
                  ...pop,
                  ...payload,
                  popTag: pop.popTag,
                }
              : pop,
          ),
        );

        return result;
      } catch (err) {
        console.error("updateCreatorPopDetails failed:", err);

        setError(getErrorMessage(err, "Unable to save Pop details."));

        return false;
      } finally {
        setSaving(false);
      }
    },
    [username],
  );

  const deleteCreatorPop = useCallback(
    async (popTag) => {
      if (!popTag || !username) {
        return false;
      }

      try {
        setDeleting(true);
        setError("");

        const response = await service.deleteCreatorPop(popTag, username);

        const result = unwrapResponse(response);

        setPops((current) =>
          current.filter((pop) => String(pop.popTag) !== String(popTag)),
        );

        setTotal((current) => Math.max(0, current - 1));

        return result;
      } catch (err) {
        console.error("deleteCreatorPop failed:", err);

        setError(getErrorMessage(err, "Unable to delete Pop."));

        return false;
      } finally {
        setDeleting(false);
      }
    },
    [username],
  );

  const loadMore = useCallback(() => {
    if (requestingRef.current || !hasMore) {
      return false;
    }

    return fetchPops({
      reset: false,
    });
  }, [fetchPops, hasMore]);

  const refresh = useCallback(
    () =>
      fetchPops({
        reset: true,
      }),
    [fetchPops],
  );

  useEffect(() => {
    requestIdRef.current += 1;
    requestingRef.current = false;

    offsetRef.current = 0;
    queryRef.current = "";

    setPops([]);
    setTotal(0);
    setHasMore(false);
    setError("");
    setSearchQuery("");

    if (username) {
      void fetchPops({
        reset: true,
        query: "",
      });
    } else {
      setLoading(false);
      setLoadingMore(false);
      setSearching(false);
    }

    return () => {
      requestIdRef.current += 1;
      requestingRef.current = false;
    };
  }, [username, fetchPops]);

  return {
    pops,
    total,

    loading,
    loadingMore,
    loadingEdit,
    saving,
    deleting,
    adding,
    searching,

    error,
    hasMore,
    searchQuery,

    getCreatorPops,
    getCreatorPopDetails,
    addCreatorPop,
    updateCreatorPopDetails,
    deleteCreatorPop,
    searchCreatorPops,

    loadMore,
    refresh,
  };
};

export default useCreatorPops;
